import {ClassNames} from '@emotion/react'
import Tooltip from '@material-ui/core/Tooltip'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import {Trans} from 'react-i18next'
import messages from '../../../assets/messages'
import {getFormattedAmount} from '../../../common/utils/general'
import {PammOfferSettingsPerformanceFeeType} from '../../../graphql/types'
import {
  TierLabel,
  TierRow,
  TierValue,
  TooltipTitle,
  TriggerButton,
  tooltipPopperCss,
} from './PerfFeeTooltip.styled'

type PerfFeeTooltipProps = {
  fees: PammOfferSettingsPerformanceFeeType | null | undefined
  currency: string
}

/**
 * Info icon with a hover tooltip that breaks the offer's performance fee down by tier.
 *
 * `performanceFees.levels` is a tiered fee structure: each entry charges `value`% once an
 * investor's equity reaches the `level` threshold, so the tooltip lists every tier.
 * Renders nothing when the offer has no fee levels.
 *
 * @param fees - The offer's performance fee settings (the `levels` array drives the tiers).
 * @param currency - ISO currency code used to format each tier's equity threshold.
 */
export const PerfFeeTooltip = ({fees, currency}: PerfFeeTooltipProps) => {
  const levels = (fees?.levels ?? []).filter((level): level is NonNullable<typeof level> => level != null)
  if (levels.length === 0) return null

  const isSingleTier = levels.length === 1
  const content = (
    <div>
      <TooltipTitle>
        <Trans {...messages.pammPerfFeeTooltipTitle} />
      </TooltipTitle>
      {levels.map((level, index) => (
        <TierRow key={index}>
          <TierLabel>
            {isSingleTier ? (
              <Trans {...messages.pammPerfFeeTooltipSingleTier} />
            ) : (
              <Trans
                {...messages.pammPerfFeeTooltipTier}
                values={{amount: getFormattedAmount({amount: level.level ?? 0, currency})}}
              />
            )}
          </TierLabel>
          <TierValue>{level.value ?? 0}%</TierValue>
        </TierRow>
      ))}
    </div>
  )

  return (
    <ClassNames>
      {({css}) => (
        <Tooltip arrow placement="top" title={content} classes={{tooltip: css(tooltipPopperCss)}}>
          <TriggerButton type="button" aria-label="Performance fee breakdown">
            <InfoOutlined style={{fontSize: 13}} />
          </TriggerButton>
        </Tooltip>
      )}
    </ClassNames>
  )
}
